import { mapState, mapGetters, mapActions } from 'vuex'

import questionHandler from './question'

const BAR_COLOR = [
    '#c4c4ff',
    '#9fede6',
    '#ffbab1',
    '#c3f0c0',
    '#eeb1ff',
    '#bddcff'
]

/**
 * A mixin to be used for calculating analysis for graphical parsing of data
 *
 * @mixin
 */
export default {

    props: {
        questionnaire: {
            type: String
        },
        parentFilter: {
            type: Object
        }
    },

    watch: {
        parentFilter(filter) {
            this.filter = filter
        }
    },

    data() {
        return {
            color: BAR_COLOR,
            filter: this.parentFilter || {},
            frontFilter: {},
            minValue: null,
            maxValue: null
        }
    },

    computed: {
        ...mapGetters('provider', [
            'getQuestionnaire',
            'getPatientList',
            'getPatientMap'
        ]),
        ...mapGetters('content', [
            'getContent',
            'getContentKey'
        ]),
        patients() {
            return this.getPatientList
        },
        records() {
			return this.getQuestionnaire(this.questionnaire)
		},
        filteredRecords() {
            if (this.records) {
                let start = (this.filter.range) ? this.filter.range[0] : null
                let end = (this.filter.range) ? this.filter.range[1] : null
                let event = this.filter.event ? this.filter.event : []
                return this.records.filter((record) => {
                    // console.log(this.filter, record)
                    if (start && start > record.time)
                        return false
                    if (end && end < record.time)
                        return false
                    if(event.length > 0 && !event.includes(record.event))
                        return false
                    return true
                })
            }
        },
        patientMap() {
            return this.getPatientMap
        },

        questionnaireContent() {
			return this.getContent('questionnaire', this.questionnaire)
		},
        questions() {
            return this.getContentKey('questionnaire', this.questionnaire, 'question')
        },
        questionnaireTitle() {
            return this.getContentKey('questionnaire', this.questionnaire, 'title')
        },

        // shifting from API to local computation so websocket updates work in real time
        analysis() {
            var analysis = {
                patient: [],
                profile: [],
                event: [],
                question: []
            };

            (this.questions || []).forEach((question) => {
                let qa = {
                    id: question.id,
                    type: question.type,
                    title: question.title,
                    question: question.question,
                    answer: [],
                    event: {}
                }
                let answers = (this.filteredRecords || [])
                    .map((record) => {
                        let userQuestion = record.question && record.question.find((q) => (q != null && q.id == question.id))
                        if (userQuestion && (userQuestion.answer || userQuestion.value))
                            return {
                                value: this.getAnswerValue(userQuestion),
                                user: record.auth,
                                time: record.completed,
                                event: record.event,
                                id: record.id,
                                problem: record.problem
                            }
                        
                    })
                    .filter((answer) => answer != null && answer.value)
                
                qa.answer = questionHandler(answers, question, this.filteredRecords)

                if(!qa || !qa.answer) {
                    return
                }
                if(question.type == 'multi_question') {
                    try {
                        qa.answer = qa.answer.map((answer) => {
                            answer.answer.forEach((subAns) => {
                               subAns.user.forEach(user => {
                                   if(!user.event || (user.event && !user.event.startsWith('v0_')))
                                       user.event = 'other'
                                   answer.event[user.event] = (answer.event[user.event] || 0) + 1
                               })
                               subAns.eventMap = Object.keys(answer.event).map((event) => ({
                                    user: subAns.user.filter(user => event === user.event),
                                    id: event
                                }))
                            })
                            
                            return answer
                       })
                    } catch (error) {
                        console.error('Error in multi-question', error)
                    }
                    
                }
                else {
                    qa.answer.push({
                        key: String(qa.answer.length + 1),
                        name: 'unanswered',
                        user: (this.filteredRecords || []).filter((record) => {
                            const userQuestion = record.question && record.question.find((q) => (q != null && q.id == question.id))
                            if(question.type === 'date' || question.type === 'unit')
                                return !userQuestion || !userQuestion.answer || (userQuestion.answer.length === 0)
                            if(question.type === 'radio')
                                return !userQuestion || !userQuestion.value
                            return !userQuestion || !userQuestion.answer
                        })
                    })
                    qa.answer.forEach((answer) => {
                        //totalUser += answer.user.length
                        // if (answer.user.length > maxUser)
                        //     maxUser = answer.user.length
                        answer.user.forEach(user => {
                            user.event = 'other'
                            if(!user.user)
                                user.user = user.auth
                            qa.event[user.event] = (qa.event[user.event] || 0) + 1
                        })
                    })
    
                    qa.answer.forEach((answer) => {
                        answer.eventMap = Object.keys(qa.event).map(event => ({
                            user: answer.user.filter(user => event === user.event),
                            id: event
                        }))
                    })
                }

                

                // if (this.isSelectQuestion(question.type) && question.select) {
                //
                //     qa.answer = question.select.map((option) => ({
                //         key: option,
                //         name: option,
                //         user: []
                //     }))
                // }


                analysis.question.push(qa)
            })

            return analysis
        }
    },

    methods: {
        ...mapActions('provider', ['loadQuestionnaire']),

        isSelectQuestion(type) {
            return type === 'large_image_select' ||
                   type === 'small_image_select' ||
                   type === 'box_select' ||
                   type === 'select'
        },

        /**
         * Takes in `analysisData` and applies `filters` to it. Also
         * parses `analysisData` along delays and answers to renders graphs for Outcome analaysis.
         *
         * @param {Object} analysisData Questionnaire analysis data from api
         * @param {Object} filters Object of filters to be applied to data
         * @param {String[]} filters.event Array of event id to be included (if left empty considered to be all)
         * @param {Number} filters.start Start date timestamp for filtering
         * @param {Number} filters.end End date timestamp for filtering
         * @returns {Array} An array of all questions parsed along delay and options
         */
        filterQuestionList(analysis, filter) {
            // Set messageDelay object (an object containing message id as keys with delays as values) for all events that are included in the eventFilter
            let messageDelay = this.analysis.profile.flatMap((profile) =>
                profile.event.flatMap((event) =>
                    (filter.event == null || filter.event.includes(event.id)) ? event.message : [] ))
                .reduce((messageDelay, message) => {
                    messageDelay[message.id] = message.delay
                    return messageDelay
                }, {})

            // Map each question to a filtered subset of its responses
            return analysis.question.map((question) => {
                let questionDisplay = {
                    answer: [],
                    delay: {},
                    total: {}
                }

                question.answer.forEach((option) => {
                    questionDisplay.answer.push( option.user.reduce((userMap, user) => {

                        // Reducing through the user response array of answer, aggregating through delays
                        user.message.forEach((message, index) => {
                            // Skip messages that are filtered out
                            if (! messageMap[message])
                                return

                            // If the message is out of the range of the filter interval
                            if ((filter.start && user.time[index] < filter.start) ||
                                (filter.end && user.time[index] > filter.end))
                                return

                            // Append to a list of results for the given delay
                            let delay = messageDelay[message]
                            if (! questionDisplay.delay[delay]) {
                                questionDisplay.delay[delay] = true
                                questionDisplay.total[delay] = []
                                userMap[delay] = []
                            }

                            userMap[delay].push(user.id)
                            questionDisplay.total[delay].push(user.id)
                        })

                    }, { ...option }))
                })

                return questionDisplay
            })
        },
        /**
         * Returns true if messageTime is between filters start and end.
         *
         * @param {Number} messageTime - Timestamp from message to be checked
         * @param {Object} filterTime - Object with start and end time to check
         * @param {Number} filterTime.start - Start time for the filter
         * @param {Number} filterTime.end - End time for the filter
         *
         * @returns {Boolean}
         */
        isWithinRange(messageTime, filterTime) {
            const { start, end} = filterTime
            if(start) {
                if(start > messageTime) return false
            }
            if(end) {
                if(end < messageTime) return false
            }
            return true
        },
        /**
         * Return the value from question response
         *
         * @param {*} userQuestion
         * @returns
         */
        getAnswerValue(userQuestion) {
            if(userQuestion.answer != null) {
                if(Array.isArray(userQuestion.answer)) {
                    return userQuestion.answer.length ? userQuestion.answer : null
                }
                    
                else if(typeof userQuestion.answer === 'object')
                    return Object.keys(userQuestion.answer).map((k) => ({ key: k, value: userQuestion.answer[k] }))
                else
                    return userQuestion.answer
            }
            else
                return userQuestion.value
        },

    }
}
